import {
  forwardRef,
  ReactElement,
  Ref,
  useCallback,
  useEffect,
  useState,
} from 'react';
import ReactSelect, {ValueType, ActionMeta} from 'react-select';
import {useDropDown} from '../../../../hooks';
import {motion} from 'framer-motion';
import {usePhoneCodes, PhoneCode} from '../../../../utils/phone-codes';

import {getColorFromTheme} from '../../../ui-utils';
import styled from 'styled-components';
import {Flex} from '../../../style/styles';
import {ArrowIconWrapper} from '../phone-select-field/styled';
import {ArrowHeadDownIcon} from '../../../atoms/icons/arrow-head-down';
import {Body1} from '../../../atoms/typography';
import {SelectFieldSecondary} from './secondary-option';
import {InputState} from '../../../interface';
import {FlagIcon} from './flag-icon';

import {SelectFieldProps, TOption} from '../option-select/option-select';

const DropdownWrapper = styled.div<{compact?: boolean}>`
  position: absolute;
  display: block;
  width: ${({compact}) => (compact ? '360px' : '400px')};
  max-width: calc(100vw - 48px);
  top: 120%;
  overflow: hidden;
  border-radius: 8px;
  border: solid 1px ${getColorFromTheme('borderDark')};
  background-color: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level2};
  isolation: isolate;
  z-index: 30;
  @media (max-width: 500px) {
    width: 250%;
  }
`;
export const PhoneCountryTrigger = styled.button<{
  state?: string;
  compact?: boolean;
}>`
  background: ${getColorFromTheme('backgroundDark')};
  border-width: 1px;
  border-style: solid;

  position: relative;
  width: ${({compact}) => (compact ? '100%' : 'auto')};
  height: ${({compact}) => (compact ? '42px' : '56px')};
  padding: ${({compact}) => (compact ? '0 12px' : '0 10px')};
  min-width: 100px;
  border-color: ${({state}) =>
    state === 'error'
      ? getColorFromTheme('red400')
      : getColorFromTheme('borderDark')};
  border-width: ${({compact}) => (compact ? '0' : '1px')};
  border-radius: ${({compact}) => (compact ? '7px 0 0 7px' : '10px 0 0 10px')};
`;

export interface TUserSelectFieldProps extends SelectFieldProps {
  label?: string;
  placeholder?: string;
  state?: InputState;
  helper?: string | ReactElement;
  compact?: boolean;
}

export const PhoneSelectFieldOption = forwardRef(
  (
    {
      state,
      options,
      onChange,
      value,
      defaultValue,
      fieldNotFoundPlaceHolder,
      compact,
      ...rest
    }: TUserSelectFieldProps,
    ref: Ref<ReactSelect<TOption>>,
  ) => {
    const phoneCodes = usePhoneCodes();
    const getValue = options.find(
      // @ts-ignore
      (option) => option.value === defaultValue || option.value === value,
    );
    const [innerValue, setInnerValue] = useState<ValueType<TOption, false>>(
      // @ts-ignore
      () => getValue,
    );
    const [selectedValue, setSelectedValue] = useState<PhoneCode>();

    // Sync innerValue when options load asynchronously (phone codes lazy load)
    useEffect(() => {
      if (
        !innerValue &&
        options.length > 0 &&
        (defaultValue !== undefined || value !== undefined)
      ) {
        const found = options.find(
          // @ts-ignore
          (option) => option.value === defaultValue || option.value === value,
        );
        if (found) {
          // @ts-ignore
          setInnerValue(found);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options.length]);
    const {handleOpen, ref: dropRef, open, handleClose} = useDropDown();

    const onSelectChange = useCallback(
      (value: ValueType<TOption, false>, action: ActionMeta<TOption>) => {
        if (onChange) {
          onChange(value, action);
        }
        setInnerValue(value);
        setSelectedValue(
          phoneCodes.find((phone) => phone.name === value?.label),
        );
        handleClose();
      },
      [handleClose, onChange, phoneCodes],
    );

    const defaultCountry = phoneCodes.find(
      (code) => code.name === innerValue?.label,
    );
    const displayCountry = selectedValue || defaultCountry;

    return (
      <>
        <div ref={dropRef} style={{position: 'relative'}}>
          <PhoneCountryTrigger
            type="button"
            compact={compact}
            onClick={handleOpen}
            state={state}>
            {displayCountry ? (
              <Flex alignItems="center" justifyContent="center">
                <Flex alignItems="center" justifyContent="center">
                  <div style={{marginTop: '2px'}}>
                    <FlagIcon country={displayCountry} />
                  </div>
                  <Body1 kind="textBody" style={{margin: '0 13px'}}>
                    {displayCountry?.dial_code}
                  </Body1>
                </Flex>
                <ArrowIconWrapper>
                  <motion.span
                    initial={{rotate: 0}}
                    animate={{
                      rotate: open ? 180 : 0,
                      marginTop: open ? '-6px' : '0px',
                    }}
                    transition={{
                      duration: 0.3,
                    }}
                    key="user-select-toggled">
                    <ArrowHeadDownIcon />
                  </motion.span>
                </ArrowIconWrapper>
              </Flex>
            ) : (
              <Flex />
            )}
          </PhoneCountryTrigger>
          {open && (
            <DropdownWrapper compact={compact} style={{zIndex: 99999}}>
              <SelectFieldSecondary
                {...rest}
                onChange={onSelectChange}
                value={innerValue}
                ref={ref as any}
                options={options}
                fieldNotFoundPlaceHolder={fieldNotFoundPlaceHolder}
              />
            </DropdownWrapper>
          )}
        </div>
      </>
    );
  },
);
